import { useCallback, useEffect, useRef, useState } from 'react';
import { App } from 'antd';

import {
	DASHBOARD_SCOPE,
	createDashboard,
	dashboardRefusal,
	deleteDashboard,
	demoteDashboard,
	getDashboard,
	getDashboardLists,
	promoteDashboard,
	updateDashboard,
} from '../services/dashboards';
import { normalizeId } from '../utils/ids';

/**
 * One collection's dashboards for the Reports page (charts A6, plan §6.1):
 * the collection's own — `dashboards`, the collection tier and the caller's
 * own (a sub-collection inherits none from above it, KPR-1084). The useCharts idiom exactly: no provider, a row is text (the list
 * carries `tileCount`, never tiles, and runs nothing).
 *
 * Writes wait for the server, which answers the dashboard as it now is (its
 * tier, its `canEdit`); a refused write leaves the list as it was. A delete
 * is the one optimistic write: the row goes at once and comes back if the
 * server refuses. `rename` and `duplicate` reject with the server's refusal
 * so the naming window can say a clash where the person is still typing.
 *
 * Duplicate reads the dashboard whole (the list has no tiles) and creates a
 * personal copy with its tiles and controls. A tile the caller cannot see
 * (`available: false`) is left out of the copy: the server judges every tile
 * of a new dashboard and would refuse the copy for it.
 */

const tierRank = (scope) => (scope === DASHBOARD_SCOPE.COLLECTION ? 0 : 1);

/** The collection tier first, then the caller's own, by name within each — the server's own order. */
export const sortDashboards = (list) => [...list].sort((a, b) => (
	tierRank(a?.scope) - tierRank(b?.scope)
	|| (a?.name || '').localeCompare(b?.name || '')
));

/** A dashboard's row from a whole dashboard: the list's shape (the tile count, never the tiles). */
export const asRow = (dashboard) => {
	if (!dashboard) return dashboard;
	const { tiles, ...rest } = dashboard;
	return Array.isArray(tiles) ? { ...rest, tileCount: tiles.length } : rest;
};

/** The tiles a copy can carry: every text tile, and the chart tiles the caller can see. */
export const copyableTiles = (tiles) => (Array.isArray(tiles) ? tiles : [])
	.filter((tile) => tile && (tile.kind === 'text' || tile.available === true))
	.map(({ id, ...tile }) => tile);

export default function useDashboards(collectionId, { enabled = true } = {}) {
	const { message } = App.useApp();
	const [dashboards, setDashboards] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [loadToken, setLoadToken] = useState(0);
	// The collection a first read has answered for (success or failure). Until
	// it has, `loading` stays true even on the render before the effect runs, so
	// a list never draws its empty state ahead of its first answer (§14).
	const [settledFor, setSettledFor] = useState(null);
	// The committed list, for a delete's rollback (a state updater may run lazily).
	const listRef = useRef(dashboards);
	listRef.current = dashboards;

	useEffect(() => {
		if (!collectionId || !enabled) {
			setDashboards([]);
			setError(null);
			setLoading(false);
			return undefined;
		}
		let active = true;
		setLoading(true);
		setError(null);
		getDashboardLists(collectionId)
			.then(({ dashboards: own }) => {
				if (!active) return;
				setDashboards(sortDashboards(own));
			})
			.catch((e) => {
				if (!active) return;
				setDashboards([]);
				setError(e);
			})
			.finally(() => {
				if (!active) return;
				setLoading(false);
				setSettledFor(collectionId);
			});
		return () => { active = false; };
	}, [collectionId, enabled, loadToken]);

	const reload = useCallback(() => setLoadToken((n) => n + 1), []);

	const replace = useCallback((updated) => {
		if (!updated || !updated._id) return;
		const row = asRow(updated);
		const id = normalizeId(row._id);
		setDashboards((current) => {
			const known = current.some((d) => normalizeId(d?._id) === id);
			return sortDashboards(known ? current.map((d) => (normalizeId(d?._id) === id ? { ...d, ...row } : d)) : [...current, row]);
		});
	}, []);

	/** Rename (and describe): rejects with the refusal, for the window to say. */
	const rename = useCallback(async (dashboard, { name, description }) => {
		const updated = await updateDashboard(collectionId, normalizeId(dashboard?._id), { name, description });
		replace(updated);
		return updated;
	}, [collectionId, replace]);

	/** A personal copy under a new name, with the tiles the caller can see: rejects with the refusal. */
	const duplicate = useCallback(async (dashboard, { name, description }) => {
		const whole = await getDashboard(collectionId, normalizeId(dashboard?._id));
		const created = await createDashboard(collectionId, {
			name,
			description,
			tiles: copyableTiles(whole?.tiles),
			controls: whole?.controls || undefined,
		});
		replace(created);
		return created;
	}, [collectionId, replace]);

	const remove = useCallback(async (dashboard) => {
		const id = normalizeId(dashboard?._id);
		const snapshot = listRef.current;
		setDashboards((current) => current.filter((d) => normalizeId(d?._id) !== id));
		try {
			await deleteDashboard(collectionId, id);
			return true;
		} catch (e) {
			setDashboards(snapshot);
			message.error(dashboardRefusal(e, 'Could not delete the dashboard.'));
			return false;
		}
	}, [collectionId, message]);

	const promote = useCallback(async (dashboard) => {
		try {
			const updated = await promoteDashboard(collectionId, normalizeId(dashboard?._id));
			replace(updated);
			return updated;
		} catch (e) {
			message.error(dashboardRefusal(e, 'Could not pin the dashboard for everyone.'));
			return null;
		}
	}, [collectionId, message, replace]);

	const demote = useCallback(async (dashboard) => {
		try {
			const updated = await demoteDashboard(collectionId, normalizeId(dashboard?._id));
			replace(updated);
			return updated;
		} catch (e) {
			message.error(dashboardRefusal(e, 'Could not unpin the dashboard.'));
			return null;
		}
	}, [collectionId, message, replace]);

	const waiting = loading || Boolean(collectionId && enabled && settledFor !== collectionId);

	return { dashboards, loading: waiting, error, reload, rename, duplicate, remove, promote, demote };
}
